import { Box, CircularProgress } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  OrganizationContext,
  useMediaUrlWithState,
  useTranslation,
} from "hhn-commons";
import MediaErrorPlaceholder from "./MediaErrorPlaceholder";

const DisplayAudio = ({
  url,
  onSetMediaUrl,
}: {
  url: string;
  onSetMediaUrl?: (url: string) => void;
}) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const mediaState = useMediaUrlWithState(url);
  const [refreshedUrl, setRefreshedUrl] = useState<string | undefined>();
  const audioRef = useRef<HTMLAudioElement>(null);
  const retriedRef = useRef(false);
  const resumePlayRef = useRef(false);

  const src = refreshedUrl ?? mediaState.data?.signedUrl;

  useEffect(() => {
    if (src) {
      onSetMediaUrl?.(src);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  // After a retry-refresh lands, resume playback automatically so the user
  // doesn't have to click play a second time.
  useEffect(() => {
    if (refreshedUrl && resumePlayRef.current && audioRef.current) {
      resumePlayRef.current = false;
      audioRef.current.play().catch(() => {
        /* autoplay blocked or raced — user can click play again */
      });
    }
  }, [refreshedUrl]);

  if (mediaState.error) {
    return (
      <MediaErrorPlaceholder
        variant="audio"
        onRetry={mediaState.retry}
        data-testid="display-audio-error"
      />
    );
  }

  if (!src) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <audio
      ref={audioRef}
      preload="metadata"
      controls
      src={src}
      data-testid="display-audio"
      style={{
        display: "block",
      }}
      onError={e => {
        const mediaError = e.currentTarget.error;
        // Code 1 = MEDIA_ERR_ABORTED. Fires when an in-flight fetch is
        // cancelled (network flap, socket reconnect, tab throttle). Not a
        // real playback failure, so don't report it.
        if (mediaError?.code === 1) return;
        // Code 2/4 is usually a stale signed URL. Fetch a fresh URL once
        // and auto-resume playback when the new src lands.
        if (
          (mediaError?.code === 2 || mediaError?.code === 4) &&
          !retriedRef.current &&
          selectedOrgUser
        ) {
          retriedRef.current = true;
          resumePlayRef.current = true;
          selectedOrgUser.assetManager.getSignedUrlInfoWithSocket(
            url,
            fresh => fresh?.signedUrl && setRefreshedUrl(fresh.signedUrl),
            true,
          );
          return;
        }
        console.error("Failed to load voice message");
        console.error(mediaError?.code, mediaError?.message);
      }}
    >
      {tNs("message.audio.notSupported")}
    </audio>
  );
};

export default DisplayAudio;
